import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Circle, LogOut, RotateCcw, Settings } from 'lucide-react';
import { projects } from '../config/projects';
import { getGlobalData } from '../utils/projectStorage';
import './Layout.css';

const Layout: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [apiConnected, setApiConnected] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  useEffect(() => {
    // Check if Anthropic API key exists
    const globalData = getGlobalData();
    setApiConnected(!!globalData.apiKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all data? This will clear all URLs, files, prompts, and chat history. Your API key will be preserved.')) {
      // Get current global data to preserve API key
      const globalData = getGlobalData();
      
      // Clear all localStorage data
      localStorage.clear();
      
      // Restore the API key in new structure
      if (globalData.apiKey) {
        const newStorageStructure = {
          global: {
            apiKey: globalData.apiKey,
            authToken: globalData.authToken
          },
          projects: {}
        };
        localStorage.setItem('appData', JSON.stringify(newStorageStructure));
      }
      
      // Refresh the page to reset all component states
      window.location.reload();
    }
  };

  // Get current project based on URL
  const getCurrentProjectId = () => {
    const pathMatch = location.pathname.match(/\/project\/([^/]+)/);
    return pathMatch ? pathMatch[1] : null;
  };

  const currentProjectId = getCurrentProjectId();

  return (
    <div className="layout">
      <header className="header">
        <div className="header-content">
          <button 
            className="admin-btn"
            onClick={() => setShowAdminPanel(!showAdminPanel)}
            title="Admin Settings"
          >
            <Settings size={20} />
          </button>
          <div className="header-title">
            <h1 className="text-white">MCode Playground</h1>
          </div>
          <div className="header-status">
            <div className="api-status">
              <Circle 
                className={`status-icon ${apiConnected ? 'connected' : 'disconnected'}`} 
                size={12}
                fill={apiConnected ? '#22c55e' : '#ef4444'}
              />
              <span>{apiConnected ? 'Connected' : 'Disconnected'}</span>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <nav className="project-tabs">
        <div className="tabs-content">
          {projects.map(project => (
            <NavLink
              key={project.id}
              to={`/project/${project.id}`}
              className={({ isActive }) => isActive ? 'project-tab active' : 'project-tab'}
            >
              <span className="tab-icon">{project.icon}</span>
              <span className="tab-name">{project.name}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <main className="main-content">
        <Outlet />
      </main>

      {/* Admin Panel Overlay */}
      {showAdminPanel && (
        <div className="admin-overlay" onClick={() => setShowAdminPanel(false)}>
          <div className="admin-panel" onClick={(e) => e.stopPropagation()}>
            <div className="admin-header">
              <h2>Admin Panel</h2>
              <button onClick={() => setShowAdminPanel(false)} className="close-btn">×</button>
            </div>
            <div className="admin-content">
              <div className="admin-section">
                <h3>Global Settings</h3>
                <button onClick={() => { navigate('/api-settings'); setShowAdminPanel(false); }} className="admin-link">
                  API Configuration
                </button>
                <button onClick={handleReset} className="admin-link danger">
                  <RotateCcw size={16} />
                  Reset All Data
                </button>
              </div>
              {currentProjectId && (
                <div className="admin-section">
                  <h3>Current Project: {projects.find(p => p.id === currentProjectId)?.name}</h3>
                  <button onClick={() => { navigate(`/project/${currentProjectId}/settings`); setShowAdminPanel(false); }} className="admin-link">
                    Project Settings
                  </button>
                  <button onClick={() => { navigate(`/project/${currentProjectId}/testing`); setShowAdminPanel(false); }} className="admin-link">
                    Testing Interface
                  </button>
                </div>
              )}
              <div className="admin-section">
                <h3>All Projects</h3>
                {projects.map(project => (
                    project.id === currentProjectId ? null :
                  <div key={project.id} className="project-admin-item">
                    <span>{project.icon} {project.name}</span>
                    <div className="project-admin-actions">
                      <button onClick={() => { navigate(`/project/${project.id}/settings`); setShowAdminPanel(false); }} className="small-btn">
                        Settings
                      </button>
                      <button onClick={() => { navigate(`/project/${project.id}/testing`); setShowAdminPanel(false); }} className="small-btn">
                        Testing
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;